import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function LocationMarker({ position, onPositionChange }) {
  const [markerPosition, setMarkerPosition] = useState(position ? [position.lat, position.lng] : null);

  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      const newPosition = [lat, lng];
      setMarkerPosition(newPosition);
      onPositionChange({ lat, lng });
    },
  });

  useEffect(() => {
    if (position) {
      setMarkerPosition([position.lat, position.lng]);
    } else {
      setMarkerPosition(null);
    }
  }, [position]);

  return markerPosition ? <Marker position={markerPosition} /> : null;
}

const LocationPicker = ({ location, onLocationChange, height = '400px', disabled = false }) => {
  const [mapCenter, setMapCenter] = useState([-1.2921, 36.8219]); // Default: Nairobi, Kenya
  const [isMapReady, setIsMapReady] = useState(false);

  useEffect(() => {
    // If location exists, center map on it
    if (location?.lat && location?.lng) {
      const lat = parseFloat(location.lat);
      const lng = parseFloat(location.lng);
      if (!isNaN(lat) && !isNaN(lng)) {
        setMapCenter([lat, lng]);
      }
    } else if (!isMapReady) {
      // Try to get user's current location on first load
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setMapCenter([position.coords.latitude, position.coords.longitude]);
            setIsMapReady(true);
          },
          () => {
            // Default to Nairobi if geolocation fails
            setMapCenter([-1.2921, 36.8219]);
            setIsMapReady(true);
          }
        );
      } else {
        setIsMapReady(true);
      }
    }
  }, [location, isMapReady]);

  const handlePositionChange = ({ lat, lng }) => {
    if (disabled) return;
    
    // Update location with new coordinates
    onLocationChange({
      lat: lat.toString(),
      lng: lng.toString(),
      city: location?.city || '', // Keep existing city or leave empty
    });
  };

  const currentPosition = location?.lat && location?.lng && 
    !isNaN(parseFloat(location.lat)) && !isNaN(parseFloat(location.lng))
    ? { lat: parseFloat(location.lat), lng: parseFloat(location.lng) }
    : null;

  return (
    <div style={{ marginTop: '8px' }}>
      <div style={{ 
        height, 
        width: '100%', 
        borderRadius: '8px', 
        overflow: 'hidden',
        border: '1px solid #e5e7eb',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto'
      }}>
        <MapContainer
          center={mapCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
          whenReady={() => setIsMapReady(true)}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationMarker
            position={currentPosition}
            onPositionChange={handlePositionChange}
          />
        </MapContainer>
      </div>
      <small className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
        {disabled ? 'Map disabled' : 'Click on the map to select the property location'}
      </small>
      {currentPosition && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
          Selected: {currentPosition.lat.toFixed(6)}, {currentPosition.lng.toFixed(6)}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
